/**
 * Clash-Off view — split-screen 1v1 bracket to decide which artist to see
 * when sets conflict. Blind mode hides names/photos; reveal shows everything.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ArtistDTO } from "../lib/api";
import type { PreferenceMap, Rating } from "../lib/preferences";
import type { ConflictEntry } from "../lib/derive";
import {
  activeMatch,
  bracketResult,
  buildBracket,
  matchesRemaining,
  resolveMatch,
  type ClashBracket,
  type ClashContestant,
} from "../lib/clashoff";
import { IconPause, IconPlay, IconX } from "../components/icons";

type Mode = "blind" | "reveal";

export function ClashOff({
  conflict,
  artists,
  onClose,
  onRate,
}: {
  conflict: ConflictEntry;
  artists: ArtistDTO[];
  onClose: () => void;
  onRate: (slug: string, rating: Rating | null) => void;
}) {
  const contestants = useMemo<ClashContestant[]>(
    () => conflict.artists.map(a => ({ slug: a.slug, name: a.name, stage: a.stage })),
    [conflict],
  );

  const [bracket, setBracket] = useState<ClashBracket>(() => buildBracket(contestants));
  const [mode, setMode] = useState<Mode>("blind");
  const [crowned, setCrowned] = useState(false);

  const artistsBySlug = useMemo(() => {
    const m = new Map<string, ArtistDTO>();
    for (const a of artists) m.set(a.slug, a);
    return m;
  }, [artists]);

  const active = activeMatch(bracket);
  const remaining = matchesRemaining(bracket);
  const totalMatchesInCurrentRound =
    active != null ? bracket.rounds[active.roundIndex]!.matches.length : 0;
  const currentMatchPosition = active
    ? bracket.rounds[active.roundIndex]!.matches
        .slice(0, active.matchIndex + 1)
        .filter(m => !m.bye).length
    : 0;
  const userDecidedPerRound = active
    ? bracket.rounds[active.roundIndex]!.matches.filter(m => !m.bye).length
    : 0;

  const pick = (side: "a" | "b") => {
    if (!active) return;
    const picked = side === "a" ? active.match.a : active.match.b;
    if (!picked) return;
    setBracket(b => resolveMatch(b, picked));
  };

  const crownWinner = () => {
    if (!bracket.champion || crowned) return;
    const { winnerSlug, loserSlugs } = bracketResult(bracket);
    if (winnerSlug) onRate(winnerSlug, "superlike");
    for (const slug of loserSlugs) onRate(slug, "meh");
    setCrowned(true);
  };

  // Auto-close a moment after crowning so the schedule can refresh.
  useEffect(() => {
    if (!crowned) return;
    const t = setTimeout(onClose, 900);
    return () => clearTimeout(t);
  }, [crowned, onClose]);

  const champion = bracket.champion;

  return (
    <div className="clashoff-backdrop" role="dialog" aria-modal="true" data-testid="clashoff">
      <div className="clashoff-sheet">
        <header className="clashoff-head">
          <div className="clashoff-head-meta">
            <span className="super">CLASH-OFF</span>
            <span className="lcd">
              D{conflict.day}
              {conflict.startLabel ? ` · ${conflict.startLabel} → ${conflict.endLabel}` : ""}
            </span>
          </div>
          <div className="clashoff-head-ctrls">
            <button
              className="chip"
              aria-pressed={mode === "blind"}
              onClick={() => setMode(mode === "blind" ? "reveal" : "blind")}
              data-testid="clashoff-toggle-mode"
            >
              {mode === "blind" ? "Blind" : "Reveal"}
            </button>
            <button
              className="chip"
              onClick={onClose}
              data-testid="clashoff-exit"
              aria-label="Exit clash-off"
            >
              <IconX />
            </button>
          </div>
        </header>

        {active && (
          <div className="clashoff-progress" data-testid="clashoff-progress">
            <span className="lcd amber">
              Round {active.roundIndex + 1}
            </span>
            <span className="lcd">
              Match {currentMatchPosition}/{userDecidedPerRound}
            </span>
            <span className="lcd">{remaining} left</span>
          </div>
        )}

        {active && (
          <div className="clashoff-split" data-testid="clashoff-split">
            <Side
              key={`${active.roundIndex}-${active.matchIndex}-a`}
              sideKey="a"
              contestant={active.match.a}
              artist={artistsBySlug.get(active.match.a.slug) ?? null}
              mode={mode}
              onPick={() => pick("a")}
            />
            <div className="clashoff-vs" aria-hidden="true">VS</div>
            <Side
              key={`${active.roundIndex}-${active.matchIndex}-b`}
              sideKey="b"
              contestant={active.match.b!}
              artist={artistsBySlug.get(active.match.b!.slug) ?? null}
              mode={mode}
              onPick={() => pick("b")}
            />
          </div>
        )}

        {champion && (
          <div className="clashoff-winner" data-testid="clashoff-winner">
            <div className="super">CHAMPION</div>
            <div className="clashoff-winner-name" data-testid="clashoff-winner-name">
              {champion.name}
            </div>
            <div className="lcd">{champion.stage}</div>
            <button
              className="share-primary-btn"
              onClick={crownWinner}
              disabled={crowned}
              data-testid="clashoff-crown"
            >
              {crowned ? "Crowned ✓" : "Crown winner"}
            </button>
            <div className="clashoff-winner-note">
              Champion is marked SUPER; losing artists are marked MEH so your schedule
              clears the conflict.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Side({
  sideKey,
  contestant,
  artist,
  mode,
  onPick,
}: {
  sideKey: "a" | "b";
  contestant: ClashContestant;
  artist: ArtistDTO | null;
  mode: Mode;
  onPick: () => void;
}) {
  const isBlind = mode === "blind";
  return (
    <div className="clashoff-side" data-testid={`clashoff-side-${sideKey}`}>
      <div className={`clashoff-card ${isBlind ? "blind" : ""}`}>
        {!isBlind && artist?.photoUrl && (
          <div
            className="clashoff-photo"
            style={{ backgroundImage: `url(${artist.photoUrl})` }}
            aria-hidden="true"
          />
        )}
        <div className="clashoff-card-body">
          {isBlind ? (
            <div className="clashoff-blind-badge">
              <div className="clashoff-blind-letter">{sideKey.toUpperCase()}</div>
              <div className="lcd">Listen blind</div>
            </div>
          ) : (
            <>
              <div className="clashoff-name">{contestant.name}</div>
              <div className="lcd">{contestant.stage}</div>
              {artist?.genre && <div className="clashoff-genre">{artist.genre}</div>}
            </>
          )}
        </div>
        <div className="clashoff-player" data-testid={`clashoff-player-${sideKey}`}>
          <AudioEmbed artist={artist} blind={isBlind} sideKey={sideKey} />
        </div>
      </div>
      <button
        className="clashoff-pick-btn"
        onClick={onPick}
        data-testid={`clashoff-pick-${sideKey}`}
        aria-label={`Pick side ${sideKey.toUpperCase()}`}
      >
        {isBlind ? `Pick ${sideKey.toUpperCase()}` : `Pick ${contestant.name}`}
      </button>
    </div>
  );
}

function AudioEmbed({
  artist,
  blind,
  sideKey,
}: {
  artist: ArtistDTO | null;
  blind: boolean;
  sideKey: "a" | "b";
}) {
  if (!artist) {
    return <div className="clashoff-no-audio">No audio available.</div>;
  }
  // DJ set (YouTube) preferred — it gets a controllable custom button in blind
  // mode via the YouTube IFrame postMessage API.
  if (artist.youtubeVideo) {
    return <YouTubeBlindPlayer videoId={artist.youtubeVideo.id} name={artist.name} blind={blind} sideKey={sideKey} />;
  }
  // Fallbacks: Deezer / SoundCloud don't expose a cross-origin control API
  // without loading their SDKs. In blind mode we shrink the iframe to show
  // only the transport strip (where play/pause lives) and hide the metadata.
  if (artist.deezerId) {
    return (
      <div className="clashoff-fallback">
        <iframe
          title={blind ? "Blind listen" : `${artist.name} on Deezer`}
          src={`https://widget.deezer.com/widget/dark/artist/${artist.deezerId}?tracklist=true`}
          height={blind ? 120 : 220}
          allow="encrypted-media; clipboard-write"
          allowTransparency
        />
        {blind && <div className="clashoff-fallback-hint">Use the widget's play/pause below.</div>}
      </div>
    );
  }
  if (artist.links.soundcloudVerified && artist.links.soundcloud) {
    return (
      <div className="clashoff-fallback">
        <iframe
          title={blind ? "Blind listen" : `${artist.name} on SoundCloud`}
          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(artist.links.soundcloud)}&color=%23ff6a13&visual=false`}
          height={blind ? 120 : 160}
          allow="autoplay; encrypted-media"
        />
        {blind && <div className="clashoff-fallback-hint">Use the widget's play/pause below.</div>}
      </div>
    );
  }
  return <div className="clashoff-no-audio">No audio available for this artist.</div>;
}

/**
 * YouTube embed with a big custom play/pause overlay. In blind mode the
 * iframe stays loaded (so it can play audio) but is hidden behind an opaque
 * veil; the overlay drives playback via the YouTube IFrame postMessage API.
 */
function YouTubeBlindPlayer({
  videoId,
  name,
  blind,
  sideKey,
}: {
  videoId: string;
  name: string;
  blind: boolean;
  sideKey: "a" | "b";
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [playing, setPlaying] = useState(false);

  // Reset play state when the video or side changes (e.g., new match).
  useEffect(() => {
    setPlaying(false);
  }, [videoId]);

  // Listen for playback-state updates from YouTube so the icon reflects reality.
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      if (!ev.origin.includes("youtube")) return;
      const f = iframeRef.current;
      if (!f || ev.source !== f.contentWindow) return;
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        // `onStateChange`: 1 = playing, 2 = paused, 0 = ended, 3 = buffering.
        if (data?.event === "infoDelivery" && typeof data?.info?.playerState === "number") {
          const s = data.info.playerState;
          if (s === 1) setPlaying(true);
          else if (s === 2 || s === 0) setPlaying(false);
        }
      } catch {
        /* ignore */
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const post = (func: "playVideo" | "pauseVideo") => {
    const f = iframeRef.current;
    if (!f || !f.contentWindow) return;
    f.contentWindow.postMessage(
      JSON.stringify({ event: "command", func, args: [] }),
      "*",
    );
  };

  const toggle = () => {
    if (playing) {
      post("pauseVideo");
      setPlaying(false);
    } else {
      post("playVideo");
      setPlaying(true);
    }
  };

  const origin = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
  const src =
    `https://www.youtube-nocookie.com/embed/${videoId}?` +
    `modestbranding=1&rel=0&controls=1&enablejsapi=1&playsinline=1&origin=${origin}`;

  return (
    <div className={`clashoff-yt ${blind ? "blind" : ""}`}>
      <iframe
        ref={iframeRef}
        title={blind ? "Blind listen" : `${name} DJ set`}
        src={src}
        height={blind ? 160 : 160}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
      {blind && (
        <button
          type="button"
          className="clashoff-play-overlay"
          onClick={toggle}
          data-testid={`clashoff-play-${sideKey}`}
          aria-label={playing ? "Pause" : "Play"}
          aria-pressed={playing}
        >
          <span className="clashoff-play-icon">
            {playing ? <IconPause /> : <IconPlay />}
          </span>
          <span className="clashoff-play-label">{playing ? "Pause" : "Play"}</span>
        </button>
      )}
    </div>
  );
}
